/**
 * Aggancia la validazione lato backend degli accessori allo store del
 * configuratore.
 *
 * L'engine risolve i vincoli localmente; questo modulo fornisce, in aggiunta, un
 * validatore che interroga il BE per verificare che l'insieme di accessori
 * scelti sia effettivamente ammissibile.
 */
import { useEffect, useRef } from 'react';
import { useApiClient } from 'src/hooks/useApiClient';
import type {
	MotornetVehicleOptionValidationResult,
	MotornetVehicleKind,
} from 'src/motornet/types';
import { useConfiguratorState } from './store';

/**
 * Funzione che valida lato BE un insieme di accessori per un dato veicolo.
 * `year`/`month` individuano il listino a cui riferire la validazione (stessi
 * parametri di /motornet/vehicle); possono essere omessi per usare il default del BE.
 */
export type Validator = (
	eurotaxCode: string,
	vehicleKind: MotornetVehicleKind,
	selectedOptionIds: number[],
	year?: number,
	month?: number
) => Promise<MotornetVehicleOptionValidationResult>;

/**
 * Hook che costruisce il {@link Validator} basato sull'API client corrente e lo
 * registra nello store del configuratore, mantenendolo aggiornato al variare
 * del client.
 */
export function useBindValidator(): void {
	const { apiClient } = useApiClient();
	const setValidator = useConfiguratorState((s) => s.setValidator);

	const clientRef = useRef(apiClient);

	useEffect(() => {
		clientRef.current = apiClient;
	}, [apiClient]);

	useEffect(() => {
		const validator: Validator = (
			eurotaxCode,
			vehicleKind,
			selectedOptionIds,
			year,
			month
		) =>
			clientRef.current.motornetControllerService.validateVehicleOptions({
				eurotaxCode,
				vehicleKind,
				selectedOptionIds,
				year,
				month,
			});
		setValidator(validator);
	}, [setValidator]);
}
